import config from '../config';

// Function to decode JWT token and normalize role
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

// Function to normalize role value
const normalizeRole = (decodedToken) => {
  // Check various possible role claim formats
  const role = decodedToken.role || 
               decodedToken.Role ||
               decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
               decodedToken['role'] ||
               decodedToken.userRole;

  // Convert role to lowercase for consistent comparison
  if (typeof role === 'string') {
    return role.toLowerCase();
  }
  return null;
};

const readResponseData = async (res) => {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const getErrorMessageFromData = (data, fallback) => {
  if (typeof data === 'string') {
    return data || fallback;
  }

  const validationErrors = data?.errors
    ? Object.values(data.errors).flat().filter(Boolean).join(' ')
    : '';

  return data?.message || validationErrors || data?.detail || data?.title || fallback;
};

const getErrorMessage = async (res, fallback) => {
  const data = await readResponseData(res);
  return getErrorMessageFromData(data, fallback);
};

// Function to get user role from JWT token
export const getUserRole = async (token) => {
  try {
    // Decode the JWT token to extract role information
    const decodedToken = decodeToken(token);
    if (!decodedToken) {
      console.error('Failed to decode JWT token');
      return null;
    }
    
    // Extract role from various possible claim formats
    let role = normalizeRole(decodedToken);
    
    // If role is not found in token, try to get it from the user endpoint
    if (!role) {
      try {
        const userId = decodedToken.sub;
        if (userId) {
          const res = await fetch(`${config.api.url}/user/${userId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          });
          
          if (res.ok) {
            const userData = await res.json();
            role = userData.role?.toLowerCase() || userData.userRole?.toLowerCase();
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
    
    // Normalize the role
    if (role === 'client_user') role = 'client';
    if (role === 'midwife_user') role = 'midwife';
    
    // Store both role and user ID
    if (role) {
      localStorage.setItem('userRole', role);
      localStorage.setItem('userId', decodedToken.sub);
    }
    
    return role;
  } catch (error) {
    console.error('Error extracting user role:', error);
    return null;
  }
};

export const register = async (email, password, firstName, lastName, phoneNumber, preferred2FAMethod) => {
  try {
    const res = await fetch(`${config.api.authUrl}/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, firstName, lastName, phoneNumber, preferred2FAMethod }),
    });

    const data = await readResponseData(res);
    
    // If the response is not ok, it's an error
    if (!res.ok) {
      return {
        ...(typeof data === 'object' ? data : {}),
        message: getErrorMessageFromData(data, 'Registration failed')
      };
    }
    
    return data;
  } catch (error) {
    return { message: 'An error occurred during registration' };
  }
};

export const registerSandbox = async (email, password, firstName, lastName, phoneNumber, preferred2FAMethod) => {
  try {
    const res = await fetch(`${config.api.authUrl}/register-sandbox`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, firstName, lastName, phoneNumber, preferred2FAMethod }),
    });

    const data = await readResponseData(res);

    if (!res.ok) {
      return {
        ...(typeof data === 'object' ? data : {}),
        message: getErrorMessageFromData(data, 'Sandbox registration failed')
      };
    }

    if (data?.token) {
      const role = await getUserRole(data.token);
      if (role) data.userRole = role;
    }

    return data;
  } catch (error) {
    return { message: 'An error occurred during sandbox registration' };
  }
};

export const login = async (email, password, forceMethod) => {
  const res = await fetch(`${config.api.authUrl}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, forceMethod }),
  });
  const data = await res.json();
  
  if (data.token) {
    try {
      // Decode token to get user ID
      const decodedToken = decodeToken(data.token);
      if (decodedToken?.sub) {
        localStorage.setItem('userId', decodedToken.sub);
      }

      // Always fetch role from API first
      const role = await getUserRole(data.token);
      
      if (role) {
        // Store normalized role
        const normalizedRole = role.toLowerCase();
        localStorage.setItem('userRole', normalizedRole);
        data.userRole = normalizedRole;
      } else {
        console.error('No role returned from API');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  }
  
  return data;
};

export const verify2FA = async (code, tempToken, rememberDevice = false) => {
  const res = await fetch(`${config.api.authUrl}/verify-2fa`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tempToken}`,
    },
    credentials: 'include',
    body: JSON.stringify({ code, rememberDevice }),
  });
  const data = await res.json();
  
  if (data.token) {
    const role = await getUserRole(data.token);
    data.userRole = role;
  }
  
  return data;
};

export const getSessionContexts = async (token) => {
  const res = await fetch(`${config.api.authUrl}/contexts`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await readResponseData(res);
  if (!res.ok) throw new Error(getErrorMessageFromData(data, 'Unable to load practice contexts'));
  return data;
};

export const selectSessionContext = async (contextReferenceId, token) => {
  const res = await fetch(`${config.api.authUrl}/context`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ contextReferenceId }),
  });
  const data = await readResponseData(res);
  if (!res.ok) throw new Error(getErrorMessageFromData(data, 'Unable to switch practice'));
  if (data.token) data.userRole = await getUserRole(data.token);
  return data;
};

export const forgotPassword = async (email) => {
  const res = await fetch(`${config.api.authUrl}/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await readResponseData(res);
  if (!res.ok) {
    throw new Error(getErrorMessageFromData(data, 'Failed to send reset email'));
  }
  return data;
};

export const resetPassword = async (token, newPassword) => {
  const res = await fetch(`${config.api.authUrl}/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword }),
  });
  const data = await readResponseData(res);
  if (!res.ok) {
    throw new Error(getErrorMessageFromData(data, 'Failed to reset password'));
  }
  return data;
};

export const verifyEmail = async (userReferenceId, token) => {
  const res = await fetch(`${config.api.authUrl}/verify-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userReferenceId, token }),
  });
  const data = await readResponseData(res);
  if (!res.ok) {
    const error = new Error(getErrorMessageFromData(data, 'Unable to verify email'));
    error.code = data?.code;
    throw error;
  }
  return data;
};

export const resendEmailVerification = async (email) => {
  const res = await fetch(`${config.api.authUrl}/resend-email-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  const data = await readResponseData(res);
  if (!res.ok) {
    const error = new Error(getErrorMessageFromData(data, 'Unable to resend verification email'));
    error.code = data?.code;
    throw error;
  }
  return data;
};

export const getCurrentSandboxTerms = async () => {
  const res = await fetch(`${config.api.authUrl}/sandbox-terms/current`);
  const data = await readResponseData(res);
  if (!res.ok) throw new Error(getErrorMessageFromData(data, 'Unable to load sandbox terms'));
  return data;
};

export const acceptSandboxTerms = async ({
  userReferenceId,
  activationToken,
  termsVersion,
  accepted,
}) => {
  const res = await fetch(`${config.api.authUrl}/sandbox-terms/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userReferenceId, activationToken, termsVersion, accepted }),
  });
  const data = await readResponseData(res);
  if (!res.ok) {
    const error = new Error(getErrorMessageFromData(data, 'Unable to accept sandbox terms'));
    error.code = data?.code;
    throw error;
  }
  return data;
};

export const validateInvite = async (token) => {
  const sessionToken = localStorage.getItem('velaToken');
  const res = await fetch(`${config.api.authUrl}/portal-invite/validate?token=${token}`, {
    headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {},
  });
  if (!res.ok) return { valid: false };
  const data = await readResponseData(res);
  return {
    valid: true,
    ...(data && typeof data === 'object' ? data : {}),
  };
};

export const acceptInvite = async (token, password) => {
  const sessionToken = localStorage.getItem('velaToken');
  const res = await fetch(`${config.api.authUrl}/portal-invite/accept`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
    },
    body: JSON.stringify({ token, password }),
  });
  const data = await readResponseData(res);
  if (!res.ok) {
    const error = new Error(getErrorMessageFromData(data, 'Failed to accept invite'));
    error.code = data?.code;
    throw error;
  }
  if (data?.token) data.userRole = await getUserRole(data.token);
  return data;
};

export const validateOrganizationInvite = async (userReferenceId, token) => {
  const res = await fetch(`${config.api.authUrl}/organization-invite/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userReferenceId, token }),
  });
  if (!res.ok) {
    const errorMessage = await res.text();
    return { valid: false, message: errorMessage };
  }
  const data = await res.json();
  return { 
    valid: true, 
    organizationInfo: {
      name: data.organizationName,
      role: data.organizationRole
    },
    userInfo: {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email
    },
    existingAccount: Boolean(data.existingAccount),
  };
};

export const acceptOrganizationInvite = async (userReferenceId, token, password) => {
  const sessionToken = sessionStorage.getItem('velaContextSelectionToken') || localStorage.getItem('velaToken');
  const res = await fetch(`${config.api.authUrl}/organization-invite/accept`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
    },
    body: JSON.stringify({ userReferenceId, token, password }),
  });
  if (!res.ok) throw new Error(await getErrorMessage(res, 'Failed to accept invite'));
};

export const updateUserDetails = async (userId, firstName, lastName) => {
  const token = localStorage.getItem('velaToken');
  const res = await fetch(`${config.api.url}/user/${userId}`, {
    method: "PUT",
    headers: { 
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({ firstName, lastName }),
  });
  if (!res.ok) {
    const errorText = await res.text();
    const msg = errorText?.trim()
      ? errorText
      : `Failed to update user (${res.status} ${res.statusText})`;
    throw new Error(msg);
  }
  return res.json();
};
